import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Two-character brand mark for the site name — `BullBuzz` → `BB`, `Forex Hub` → `FH`.
 *
 * Used wherever the real logo or favicon is missing, or fails to load, so the header,
 * the preloader and the boot shell all fall back to the *site's* mark rather than to a
 * hardcoded one. Those fallbacks used to read `FX`, which was the previous site's
 * branding and meant a missing icon silently advertised the wrong company.
 *
 * It splits on camel case as well as spaces because a one-word brand is the normal
 * case here: "BullBuzz" has to read as two initials, not "BU". A single all-lowercase
 * token offers no boundary to find, so it degrades to its first two letters.
 *
 * `fallback` is returned for an empty name, which is what the preloader sees before
 * `/api/seo` has answered.
 */
export function siteInitials(name?: string | null, fallback = "BB") {
  const raw = String(name || "").trim();
  if (!raw) return fallback

  const words = raw
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .split(/[\s._-]+/)
    .map((w) => w.trim())
    .filter(Boolean)

  if (!words.length) return fallback
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase()
  return (words[0][0] + words[1][0]).toUpperCase()
}
